import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../store';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const API = (() => {
  const h = window.location.hostname;
  return process.env.REACT_APP_API_URL || ((h === 'localhost' || h === '127.0.0.1') ? 'http://localhost:5001/api' : `http://${h}:5001/api`);
})();

interface SummaryData {
  period: string;
  total_transactions: number;
  total_revenue: number;
  total_gross_revenue?: number;
  total_discount: number;
  total_tax: number;
  average_transaction: number;
  by_branch: Array<{ branch_name: string; transactions: number; revenue: number; hpp: number }>;
  total_hpp?: number;
  by_payment: Array<{ method: string; count: number; amount: number }>;
}

interface DailySale {
  date: string;
  total_transactions: string;
  total_sales: string;
  branch_name?: string;
}

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val);

const paymentLabel = (method: string) => {
  const m: Record<string, string> = { cash: 'Tunai', qris: 'QRIS', transfer: 'Transfer', card: 'Kartu', multiple: 'Kombinasi', free: 'Gratis' };
  return m[method] || method.toUpperCase();
};

const PAYMENT_COLORS: Record<string, string> = {
  cash: '#27ae60', qris: '#1A73E8', transfer: '#f39c12', card: '#9b59b6', multiple: '#e67e22', free: '#e67e22',
};

export const ReportsPage: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const isSuperAdmin = ['super_admin', 'finance', 'accounting'].includes(user?.role || '');
  const canFilterAllBranches = ['super_admin', 'finance', 'accounting'].includes(user?.role || '');
  const [activeTab, setActiveTab] = useState<'transaksi' | 'addons' | 'stok' | 'void' | 'bundling' | 'free'>('transaksi');
  const [addonTypeFilter, setAddonTypeFilter] = useState<'all' | 'sewa' | 'jual'>('all');
  const [dateMode, setDateMode] = useState<'month' | 'custom'>('month');
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [customStart, setCustomStart] = useState(() => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
  const [customEnd, setCustomEnd] = useState(() => now.toISOString().slice(0, 10));
  const [selectedBranch, setSelectedBranch] = useState(canFilterAllBranches ? 'all' : (user?.branch_id || 'all'));
  const [selectedPayment, setSelectedPayment] = useState('all');
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [dailySales, setDailySales] = useState<DailySale[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [productSales, setProductSales] = useState<any[]>([]);
  const [stockReport, setStockReport] = useState<any[]>([]);
  const [voidedReport, setVoidedReport] = useState<any[]>([]);
  const [voidedSummary, setVoidedSummary] = useState({ total_voided: 0, total_amount: 0 });
  const [bundledReport, setBundledReport] = useState<any[]>([]);
  const [bundledSummary, setBundledSummary] = useState({ total_bundled: 0, total_amount: 0 });
  const [freeReport, setFreeReport] = useState<any[]>([]);
  const [freeSummary, setFreeSummary] = useState({ total_free: 0, total_discount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const derivedStartDate = useMemo(() => {
    if (dateMode === 'custom') return customStart;
    const [year, month] = selectedMonth.split('-');
    return `${year}-${month}-01`;
  }, [dateMode, selectedMonth, customStart]);

  const derivedEndDate = useMemo(() => {
    if (dateMode === 'custom') return customEnd;
    const [year, month] = selectedMonth.split('-');
    const lastDay = new Date(Number(year), Number(month), 0).getDate();
    return `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
  }, [dateMode, selectedMonth, customEnd]);

  const periodLabel = dateMode === 'month' ? selectedMonth : `${customStart} s/d ${customEnd}`;

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user]);

  useEffect(() => {
    fetchAll();
  }, [derivedStartDate, derivedEndDate, selectedBranch, activeTab]);

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    const headers = { Authorization: `Bearer ${token}` };
    const startDate = derivedStartDate;
    const endDate = derivedEndDate;
    const branchParam = selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : '';
    const branchQ = selectedBranch !== 'all' ? `?branch_id=${selectedBranch}` : '';

    try {
      const branchRes = await axios.get(`${API}/branches`, { headers }).then(r => r.data).catch(() => []);
      setBranches(Array.isArray(branchRes) ? branchRes : []);

      if (activeTab === 'transaksi') {
        const [summaryRes, dailyRes, productSalesRes] = await Promise.all([
          axios.get(`${API}/reports/summary?period=month&start_date=${startDate}&end_date=${endDate}${branchParam}`, { headers }).then(r => r.data.data || r.data).catch(() => null),
          axios.get(`${API}/reports/sales?start_date=${startDate}&end_date=${endDate}${branchParam}`, { headers }).then(r => r.data.data || r.data).catch(() => []),
          axios.get(`${API}/reports/product-sales?start_date=${startDate}&end_date=${endDate}${branchParam}`, { headers }).then(r => r.data.data || []).catch(() => []),
        ]);
        if (!summaryRes) throw new Error('Gagal memuat laporan');
        setSummary(summaryRes);
        setDailySales(Array.isArray(dailyRes) ? dailyRes : []);
        setProductSales(Array.isArray(productSalesRes) ? productSalesRes : []);

      } else if (activeTab === 'addons') {
        const res = await axios.get(`${API}/reports/product-sales?start_date=${startDate}&end_date=${endDate}${branchParam}`, { headers }).then(r => r.data.data || []).catch(() => []);
        setProductSales(Array.isArray(res) ? res : []);

      } else if (activeTab === 'stok') {
        const res = await axios.get(`${API}/reports/stock${branchQ}`, { headers }).then(r => r.data.data || []).catch(() => []);
        setStockReport(Array.isArray(res) ? res : []);

      } else if (activeTab === 'void') {
        const res = await axios.get(`${API}/reports/voided?start_date=${startDate}&end_date=${endDate}${branchParam}`, { headers }).then(r => r.data).catch(() => ({ data: [], summary: {} }));
        setVoidedReport(Array.isArray(res.data) ? res.data : []);
        setVoidedSummary(res.summary || { total_voided: 0, total_amount: 0 });

      } else if (activeTab === 'bundling') {
        const res = await axios.get(`${API}/reports/bundled?start_date=${startDate}&end_date=${endDate}${branchParam}`, { headers }).then(r => r.data).catch(() => ({ data: [], summary: {} }));
        setBundledReport(Array.isArray(res.data) ? res.data : []);
        setBundledSummary(res.summary || { total_bundled: 0, total_amount: 0 });
      } else if (activeTab === 'free') {
        const res = await axios.get(`${API}/reports/free?start_date=${startDate}&end_date=${endDate}${branchParam}`, { headers }).then(r => r.data).catch(() => ({ data: [], summary: {} }));
        setFreeReport(Array.isArray(res.data) ? res.data : []);
        setFreeSummary(res.summary || { total_free: 0, total_discount: 0 });
      }
    } catch (err: any) {
      setError('Gagal memuat laporan. Pastikan server backend berjalan.');
    } finally {
      setLoading(false);
    }
  };

  // Aggregate daily sales by date (sum across branches)
  const monthlyAggregated = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of dailySales) {
      const monthKey = d.date?.slice(0, 7) || ''; // YYYY-MM
      map.set(monthKey, (map.get(monthKey) || 0) + Number(d.total_sales || 0));
    }
    const months = Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    return months.map(([monthKey, revenue]) => {
      const [year, month] = monthKey.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[parseInt(month) - 1] || '';
      return {
        monthKey,
        label: `${monthName} ${year}`,
        revenue,
      };
    });
  }, [dailySales]);

  // Daily sales aggregated by date (sum across branches)
  const dailyAggregated = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of dailySales) {
      const dateKey = d.date?.slice(0, 10) || '';
      if (!dateKey) continue;
      map.set(dateKey, (map.get(dateKey) || 0) + Number(d.total_sales || 0));
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, revenue]) => ({ date, revenue }));
  }, [dailySales]);

  // Branch status: rank by revenue
  const branchWithStatus = useMemo(() => {
    if (!summary) return [];
    const sorted = [...summary.by_branch].sort((a, b) => b.revenue - a.revenue);
    const total = sorted.length;
    return sorted.map((b, i) => {
      const rank = i / Math.max(total - 1, 1);
      const status = rank < 0.34 ? 'capai' : rank < 0.67 ? 'hampir' : 'bawah';
      const topPayment = summary.by_payment[0]?.method || 'cash';
      return { ...b, status, topPayment };
    });
  }, [summary]);

  const highestBranchRevenue = useMemo(() => {
    if (!summary || summary.by_branch.length === 0) return 0;
    return Math.max(...summary.by_branch.map(b => b.revenue));
  }, [summary]);

  const handleExportExcel = async () => {
    if (!summary) return;
    const authHeaders = { Authorization: `Bearer ${token}` };
    const startDate = derivedStartDate;
    const endDate = derivedEndDate;
    const branchParam = selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : '';

    const [txData, prodData] = await Promise.all([
      axios.get(`${API}/transactions?start_date=${startDate}&end_date=${endDate}${branchParam}`, { headers: authHeaders }).then(r => (r.data.data || []).filter((t: any) => !t.is_voided)).catch(() => []),
      axios.get(`${API}/reports/product-sales?start_date=${startDate}&end_date=${endDate}${branchParam}`, { headers: authHeaders }).then(r => r.data.data || []).catch(() => []),
    ]);

    const branchLabel = selectedBranch === 'all' ? 'Semua Cabang' : (branches.find((b: any) => b.id === selectedBranch)?.name || '');
    const printed = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    const rp = (v: number) => `Rp ${Math.round(v).toLocaleString('id-ID')}`; // string — for descriptive/label rows only
    const n = (v: number) => Math.round(v);   // actual number — sumable in Excel
    const num = (v: number) => Math.round(v); // count as number

    // ── Group txData by date for daily recap ──
    const dailyMap = new Map<string, { branch: string; transactions: any[] }[]>();
    (txData as any[]).forEach(tx => {
      const date = new Date(tx.created_at).toLocaleDateString('id-ID');
      const branch = tx.branch_name || '';
      if (!dailyMap.has(date)) dailyMap.set(date, []);
      let group = dailyMap.get(date)!.find(g => g.branch === branch);
      if (!group) { group = { branch, transactions: [] }; dailyMap.get(date)!.push(group); }
      group.transactions.push(tx);
    });

    const rows: any[][] = [];

    const totalHppSummary = summary.total_hpp || 0;
    const totalLabaSummary = summary.total_revenue - totalHppSummary;
    const marginSummary = summary.total_revenue > 0 ? (totalLabaSummary / summary.total_revenue * 100) : 0;
    const totalGross = summary.total_gross_revenue ?? summary.total_revenue;
    const hppCell = (v: number) => v > 0 ? n(v) : '-';
    const labaCell = (laba: number, hpp: number) => hpp > 0 ? n(laba) : '-';
    const marginCell = (margin: number, hpp: number) => hpp > 0 ? Number(Number(margin).toFixed(2)) : '-';

    // ── Header ──
    rows.push(['REKAP PENJUALAN', periodLabel]);
    rows.push([`Periode: ${startDate} s/d ${endDate}`, '', `Cabang: ${branchLabel}`, '', `Dicetak: ${printed}`]);
    rows.push([]);

    // ── Laporan Laba Rugi ──
    rows.push(['LAPORAN LABA RUGI']);
    if (totalGross !== summary.total_revenue) {
      rows.push(['Omzet Kotor (sebelum diskon)', n(totalGross)]);
      rows.push(['Diskon Diberikan', n(-summary.total_discount)]);
      if (summary.total_tax > 0) rows.push(['Pajak', n(summary.total_tax)]);
    }
    rows.push(['Pendapatan Bersih', n(summary.total_revenue)]);
    rows.push(['HPP (Harga Pokok Pembelian)', n(-totalHppSummary)]);
    rows.push(['Laba Bersih', n(totalLabaSummary)]);
    rows.push(['Margin', `${marginSummary.toFixed(2)}%`]);
    rows.push([]);

    // ── Ringkasan ──
    rows.push(['RINGKASAN']);
    rows.push(['Total Pendapatan (Rp)', 'Total HPP (Rp)', 'Laba Bersih (Rp)', 'Margin', 'Total Transaksi', 'Rata-rata per Transaksi (Rp)', 'Total Diskon (Rp)']);
    rows.push([n(summary.total_revenue), n(totalHppSummary), n(totalLabaSummary), `${marginSummary.toFixed(2)}%`, num(summary.total_transactions), n(summary.average_transaction), n(summary.total_discount)]);
    rows.push([]);

    // ── Per Cabang ──
    rows.push(['PER CABANG']);
    rows.push(['Cabang', 'Total Transaksi', 'Total Omzet (Rp)', 'HPP (Rp)', 'Laba (Rp)', 'Margin', 'Rata-rata per Transaksi (Rp)']);
    summary.by_branch.forEach(b => {
      const bLaba = b.revenue - (b.hpp || 0);
      const bMargin = b.revenue > 0 ? (bLaba / b.revenue * 100) : 0;
      rows.push([b.branch_name, num(b.transactions), n(b.revenue), n(b.hpp || 0), n(bLaba), `${bMargin.toFixed(2)}%`, n(b.transactions > 0 ? b.revenue / b.transactions : 0)]);
    });
    rows.push(['TOTAL', num(summary.total_transactions), n(summary.total_revenue), n(totalHppSummary), n(totalLabaSummary), `${marginSummary.toFixed(2)}%`, n(summary.average_transaction)]);
    rows.push([]);

    // ── Metode Pembayaran ──
    rows.push(['METODE PEMBAYARAN']);
    rows.push(['Metode', 'Jumlah Transaksi', 'Total (Rp)', 'Persentase']);
    const payTotalAmt = summary.by_payment.reduce((s, p) => s + p.amount, 0);
    summary.by_payment.forEach(p => {
      const pct = payTotalAmt > 0 ? (p.amount / payTotalAmt * 100) : 0;
      rows.push([paymentLabel(p.method), num(p.count), n(p.amount), `${pct.toFixed(2)}%`]);
    });
    rows.push(['TOTAL', num(summary.by_payment.reduce((s, p) => s + p.count, 0)), n(payTotalAmt), '100.00%']);
    rows.push([]);

    // ── Rekap Harian ──
    rows.push(['REKAP HARIAN']);
    rows.push(['Tanggal', 'Cabang', 'Jml Transaksi', 'Total Omzet (Rp)', 'Tunai (Rp)', 'QRIS (Rp)', 'Transfer (Rp)', 'Kombinasi/Lain (Rp)']);
    Array.from(dailyMap.entries()).reverse().forEach(([date, groups]) => {
      groups.forEach(g => {
        const byMethod = (method: string) => g.transactions.filter(t => t.payment_method === method).reduce((s: number, t: any) => s + Number(t.total_amount || 0), 0);
        const others = g.transactions.filter(t => !['cash','qris','transfer'].includes(t.payment_method)).reduce((s: number, t: any) => s + Number(t.total_amount || 0), 0);
        rows.push([
          date,
          g.branch,
          num(g.transactions.length),
          n(g.transactions.reduce((s: number, t: any) => s + Number(t.total_amount || 0), 0)),
          byMethod('cash') > 0 ? n(byMethod('cash')) : '-',
          byMethod('qris') > 0 ? n(byMethod('qris')) : '-',
          byMethod('transfer') > 0 ? n(byMethod('transfer')) : '-',
          others > 0 ? n(others) : '-',
        ]);
      });
    });
    rows.push([]);

    // ── Penjualan Produk: Sewa & Beli ──
    const sewaItems = (prodData as any[]).filter(p => p.category_name?.toLowerCase() === 'jasa');
    const beliItems = (prodData as any[]).filter(p => p.category_name?.toLowerCase() !== 'jasa');
    const totalSewa = sewaItems.reduce((s: number, p: any) => s + Number(p.total_sales || 0), 0);
    const totalBeli = beliItems.reduce((s: number, p: any) => s + Number(p.total_sales || 0), 0);
    const totalHppSewa = sewaItems.reduce((s: number, p: any) => s + (Number(p.unit_cost || 0) > 0 ? Number(p.hpp_total || 0) : 0), 0);
    const totalHppBeli = beliItems.reduce((s: number, p: any) => s + (Number(p.unit_cost || 0) > 0 ? Number(p.hpp_total || 0) : 0), 0);
    const totalLabaSewa = sewaItems.reduce((s: number, p: any) => s + (Number(p.unit_cost || 0) > 0 ? Number(p.total_laba || 0) : 0), 0);
    const totalLabaBeli = beliItems.reduce((s: number, p: any) => s + (Number(p.unit_cost || 0) > 0 ? Number(p.total_laba || 0) : 0), 0);

    rows.push(['PENJUALAN SEWA / JASA']);
    rows.push(['Produk', 'Qty Terjual', 'Total Omzet (Rp)', 'HPP/Unit (Rp)', 'HPP Total (Rp)', 'Laba (Rp)', 'Margin %', 'Harga Rata-rata (Rp)', 'Transaksi']);
    if (sewaItems.length === 0) {
      rows.push(['-', '-', '-', '-', '-', '-', '-', '-', '-']);
    } else {
      sewaItems.forEach(p => {
        const hpp = Number(p.unit_cost || 0);
        rows.push([
          p.name,
          num(Number(p.total_quantity || 0)),
          n(Number(p.total_sales || 0)),
          hppCell(hpp),
          labaCell(Number(p.hpp_total || 0), hpp),
          labaCell(Number(p.total_laba || 0), hpp),
          marginCell(Number(p.margin_percent || 0), hpp),
          n(Number(p.avg_price || 0)),
          num(Number(p.total_transactions || 0)),
        ]);
      });
      rows.push(['SUBTOTAL SEWA', '', n(totalSewa), '', n(totalHppSewa), n(totalLabaSewa), '', '', '']);
    }
    rows.push([]);

    rows.push(['PENJUALAN BELI (Konsumsi & Peralatan)']);
    rows.push(['Produk', 'Tipe', 'Qty Terjual', 'Total Omzet (Rp)', 'HPP/Unit (Rp)', 'HPP Total (Rp)', 'Laba (Rp)', 'Margin %', 'Harga Rata-rata (Rp)', 'Transaksi']);
    if (beliItems.length === 0) {
      rows.push(['-', '-', '-', '-', '-', '-', '-', '-', '-', '-']);
    } else {
      beliItems.forEach(p => {
        const hpp = Number(p.unit_cost || 0);
        rows.push([
          p.name,
          p.category_name || 'Konsumsi',
          num(Number(p.total_quantity || 0)),
          n(Number(p.total_sales || 0)),
          hppCell(hpp),
          labaCell(Number(p.hpp_total || 0), hpp),
          labaCell(Number(p.total_laba || 0), hpp),
          marginCell(Number(p.margin_percent || 0), hpp),
          n(Number(p.avg_price || 0)),
          num(Number(p.total_transactions || 0)),
        ]);
      });
      rows.push(['SUBTOTAL BELI', '', '', n(totalBeli), '', n(totalHppBeli), n(totalLabaBeli), '', '', '']);
    }
    rows.push([]);

    const totalHppProduk = totalHppSewa + totalHppBeli;
    const totalLabaProduk = totalLabaSewa + totalLabaBeli;
    const totalOmzetProduk = totalSewa + totalBeli;
    rows.push(['TOTAL PRODUK & ADD-ONS', '', '', n(totalOmzetProduk), '', n(totalHppProduk), n(totalLabaProduk), '', '', '']);
    rows.push(['* Catatan: Total Pendapatan mencakup biaya sewa lapangan/booking yang tidak tercatat di tabel produk']);
    rows.push([]);

    // ── Detail Transaksi ──
    rows.push(['DETAIL TRANSAKSI']);
    rows.push(['No', 'Tanggal', 'Cabang', 'Kasir', 'Konsumen', 'Metode Bayar', 'Total (Rp)', 'Diskon (Rp)', 'Status']);
    (txData as any[]).forEach((tx, i) => {
      rows.push([
        i + 1,
        new Date(tx.created_at).toLocaleDateString('id-ID'),
        tx.branch_name || '',
        tx.cashier_name || '',
        tx.customer_name || '-',
        paymentLabel(tx.payment_method),
        n(Number(tx.total_amount || 0)),
        n(Number(tx.discount_amount || 0)),
        tx.is_voided ? 'Void' : 'Lunas',
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rekap Penjualan');
    XLSX.writeFile(wb, `rekap-penjualan-${periodLabel.replace(/[^a-zA-Z0-9-]/g, '-')}.xlsx`);
  };

  const handleExportPDF = () => {
    if (!summary) return;

    // P&L: total_revenue is already net of discount (= subtotal - discount + tax).
    // Use summary.total_hpp from the corrected backend CTE query.
    const totalRevenue = summary.total_revenue;
    const totalGrossRevenue = summary.total_gross_revenue ?? totalRevenue;
    const totalDiscount = summary.total_discount || 0;
    const totalTax = summary.total_tax || 0;
    const totalHpp = summary.total_hpp || 0;
    const netProfit = totalRevenue - totalHpp;
    const marginPercent = totalRevenue > 0 ? (netProfit / totalRevenue * 100) : 0;
    // Payment % denominator: sum of actual payment amounts (not inflated total_revenue)
    const totalPaymentsAmt = summary.by_payment.reduce((s: number, p: any) => s + Number(p.amount), 0);

    const printContent = `
      <html><head><title>Laporan ${selectedMonth}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 24px; color: #1e293b; }
        h1 { font-size: 20px; margin-bottom: 4px; }
        h2 { font-size: 15px; margin-top: 20px; margin-bottom: 8px; }
        p.sub { color: #64748b; font-size: 13px; margin-bottom: 24px; }
        .metrics { display: flex; gap: 16px; margin-bottom: 24px; }
        .metric { border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; flex: 1; }
        .metric-label { font-size: 11px; text-transform: uppercase; color: #94a3b8; margin-bottom: 4px; }
        .metric-value { font-size: 22px; font-weight: bold; color: #1A73E8; }
        .pl-section { background: #f8fafc; padding: 16px; border-radius: 8px; margin: 20px 0; }
        .pl-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
        .pl-row.total { font-weight: bold; border-top: 2px solid #1e293b; border-bottom: none; padding: 12px 0; }
        .pl-row.positive { color: #27ae60; }
        .pl-row.negative { color: #e74c3c; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        th { background: #f8fafc; padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; color: #94a3b8; border-bottom: 2px solid #e2e8f0; }
        td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; font-size: 13px; }
        tfoot td { font-weight: bold; border-top: 2px solid #e2e8f0; background: #f8fafc; }
        @page { margin: 0.8cm; size: A4; }
        @media print { body { margin: 0; } }
      </style></head><body>
      <h1>Laporan Penjualan Bulanan — ${selectedMonth}</h1>
      <p class="sub">Dicetak: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
      <div class="metrics">
        <div class="metric"><div class="metric-label">Total Pendapatan</div><div class="metric-value">${formatCurrency(summary.total_revenue)}</div></div>
        <div class="metric"><div class="metric-label">Total HPP</div><div class="metric-value" style="color:#e67e22">${formatCurrency(totalHpp)}</div></div>
        <div class="metric"><div class="metric-label">Laba Bersih</div><div class="metric-value" style="color:${netProfit >= 0 ? '#27ae60' : '#e74c3c'}">${formatCurrency(netProfit)}</div></div>
        <div class="metric"><div class="metric-label">Total Transaksi</div><div class="metric-value">${summary.total_transactions.toLocaleString('id-ID')}</div></div>
        <div class="metric"><div class="metric-label">Rata-rata / Transaksi</div><div class="metric-value">${formatCurrency(summary.average_transaction)}</div></div>
      </div>

      <h2>Laporan Laba Rugi</h2>
      <div class="pl-section">
        ${totalGrossRevenue !== totalRevenue ? `<div class="pl-row">
          <span>Omzet Kotor (sebelum diskon)</span>
          <span>${formatCurrency(totalGrossRevenue)}</span>
        </div>
        ${totalDiscount > 0 ? `<div class="pl-row negative">
          <span>Diskon Diberikan</span>
          <span>- ${formatCurrency(totalDiscount)}</span>
        </div>` : ''}
        ${totalTax > 0 ? `<div class="pl-row">
          <span>Pajak</span>
          <span>+ ${formatCurrency(totalTax)}</span>
        </div>` : ''}` : ''}
        <div class="pl-row">
          <span>Pendapatan Bersih</span>
          <span>${formatCurrency(totalRevenue)}</span>
        </div>
        <div class="pl-row negative">
          <span>HPP (Harga Pokok Pembelian)</span>
          <span>- ${formatCurrency(totalHpp)}</span>
        </div>
        <div class="pl-row total ${netProfit >= 0 ? 'positive' : 'negative'}">
          <span>Laba Bersih (Net Profit)</span>
          <span>${netProfit >= 0 ? '+' : ''} ${formatCurrency(netProfit)}</span>
        </div>
        <div class="pl-row">
          <span>Margin Keuntungan</span>
          <span>${marginPercent.toFixed(2)}%</span>
        </div>
      </div>

      <h2>Performa Cabang</h2>
      <table>
        <thead><tr><th>Cabang</th><th style="text-align:right">Pendapatan</th><th style="text-align:right">HPP</th><th style="text-align:right">Laba</th><th style="text-align:right">Margin</th><th style="text-align:center">Transaksi</th><th style="text-align:right">Rata-rata</th></tr></thead>
        <tbody>${summary.by_branch.map(b => {
          const bLaba = b.revenue - (b.hpp || 0);
          const bMargin = b.revenue > 0 ? (bLaba / b.revenue * 100) : 0;
          return `<tr>
            <td>${b.branch_name}</td>
            <td style="text-align:right">${formatCurrency(b.revenue)}</td>
            <td style="text-align:right;color:#e67e22">${formatCurrency(b.hpp || 0)}</td>
            <td style="text-align:right;color:${bLaba >= 0 ? '#27ae60' : '#e74c3c'}">${formatCurrency(bLaba)}</td>
            <td style="text-align:right">${bMargin.toFixed(2)}%</td>
            <td style="text-align:center">${b.transactions.toLocaleString('id-ID')}</td>
            <td style="text-align:right">${formatCurrency(b.transactions > 0 ? b.revenue / b.transactions : 0)}</td>
          </tr>`;
        }).join('')}</tbody>
        <tfoot><tr>
          <td><strong>TOTAL</strong></td>
          <td style="text-align:right"><strong>${formatCurrency(summary.total_revenue)}</strong></td>
          <td style="text-align:right;color:#e67e22"><strong>${formatCurrency(totalHpp)}</strong></td>
          <td style="text-align:right;color:${netProfit >= 0 ? '#27ae60' : '#e74c3c'}"><strong>${formatCurrency(netProfit)}</strong></td>
          <td style="text-align:right"><strong>${marginPercent.toFixed(2)}%</strong></td>
          <td style="text-align:center"><strong>${summary.total_transactions.toLocaleString('id-ID')}</strong></td>
          <td style="text-align:right"><strong>${formatCurrency(summary.average_transaction)}</strong></td>
        </tr></tfoot>
      </table>

      <h2>Detail Produk & Add-Ons</h2>
      <table>
        <thead><tr>
          <th>Kode</th>
          <th>Produk</th>
          <th>Qty</th>
          <th>Harga/Pcs</th>
          <th>Total Penjualan</th>
          <th>Harga Pokok Pembelian</th>
          <th>Total Laba</th>
          <th>Margin %</th>
        </tr></thead>
        <tbody>${productSales.map((p: any) => {
          const hpp = Number(p.unit_cost || 0);
          const qty = Number(p.total_quantity || 0);
          const avgPrice = Number(p.avg_price || 0);
          const totalSales = Number(p.total_sales || 0);
          const totalLaba = Number(p.total_laba || 0);
          const margin = Number(p.margin_percent || 0);
          const konversi = Number(p.konversi || 1);
          const unitBesar = p.unit_besar || '';
          const hargaPerPcs = avgPrice;
          const hargaDisplay = (unitBesar && konversi > 1)
            ? `${formatCurrency(Math.round(hargaPerPcs * konversi))}/${konversi}`
            : formatCurrency(hargaPerPcs);
          const noHpp = hpp === 0;
          return `<tr>
            <td style="font-family: monospace; font-size: 11px; color: #475569;">${p.kode_barang || '—'}</td>
            <td>${p.name}</td>
            <td style="text-align: right;">${qty.toLocaleString('id-ID')}</td>
            <td style="text-align: right;">${hargaDisplay}</td>
            <td style="text-align: right;">${formatCurrency(totalSales)}</td>
            <td style="text-align: right; color: ${noHpp ? '#94a3b8' : 'inherit'};">${noHpp ? '— *' : formatCurrency(hpp)}</td>
            <td style="text-align: right; color: ${noHpp ? '#94a3b8' : totalLaba >= 0 ? '#27ae60' : '#e74c3c'};">${noHpp ? '—' : `<strong>${totalLaba >= 0 ? '+' : ''} ${formatCurrency(totalLaba)}</strong>`}</td>
            <td style="text-align: right; color: ${noHpp ? '#94a3b8' : margin >= 30 ? '#27ae60' : '#f39c12'};">${noHpp ? '—' : `<strong>${margin.toFixed(2)}%</strong>`}</td>
          </tr>`;
        }).join('')}</tbody>
        <tfoot>
          <tr>
            <td colspan="4"><strong>TOTAL</strong></td>
            <td style="text-align: right;"><strong>${formatCurrency(productSales.reduce((sum: number, p: any) => sum + Number(p.total_sales || 0), 0))}</strong></td>
            <td colspan="1"></td>
            <td style="text-align: right;"><strong>${formatCurrency(productSales.reduce((sum: number, p: any) => sum + Number(p.total_laba || 0), 0))}</strong></td>
            <td style="text-align: right;"><strong>${(productSales.reduce((sum: number, p: any) => sum + Number(p.total_laba || 0), 0) / (productSales.reduce((sum: number, p: any) => sum + Number(p.total_sales || 0), 0) || 1) * 100).toFixed(2)}%</strong></td>
          </tr>
          <tr><td colspan="8" style="font-size: 11px; color: #94a3b8; font-style: italic; padding-top: 6px;">* HPP belum diisi — laba &amp; margin tidak ditampilkan untuk item tersebut</td></tr>
        </tfoot>
      </table>

      <h2>Pembayaran per Metode</h2>
      <table>
        <thead><tr>
          <th>Metode Pembayaran</th>
          <th>Jumlah Transaksi</th>
          <th>Total Jumlah</th>
          <th>Persentase</th>
        </tr></thead>
        <tbody>${summary.by_payment.map((p: any) => {
          const pct = totalPaymentsAmt > 0 ? (Number(p.amount) / totalPaymentsAmt * 100) : 0;
          return `<tr>
            <td>${paymentLabel(p.method)}</td>
            <td style="text-align: right;">${p.count.toLocaleString('id-ID')}</td>
            <td style="text-align: right;">${formatCurrency(p.amount)}</td>
            <td style="text-align: right;">${pct.toFixed(2)}%</td>
          </tr>`;
        }).join('')}</tbody>
        <tfoot><tr>
          <td><strong>TOTAL</strong></td>
          <td style="text-align: right;"><strong>${summary.by_payment.reduce((sum: number, p: any) => sum + p.count, 0).toLocaleString('id-ID')}</strong></td>
          <td style="text-align: right;"><strong>${formatCurrency(totalPaymentsAmt)}</strong></td>
          <td style="text-align: right;"><strong>100.00%</strong></td>
        </tr></tfoot>
      </table>
      </body></html>
    `;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(printContent);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 400);
  };

  const handleExportAddons = () => {
    const wb = XLSX.utils.book_new();
    const rows = productSales.map((p: any) => ({
      'Kode Barang': p.kode_barang || '',
      'Produk': p.name,
      'Kategori': p.category_name || '',
      'Qty Terjual': Number(p.total_quantity || 0),
      'Total Penjualan (Rp)': Number(p.total_sales || 0),
      'HPP / Unit Cost (Rp)': Number(p.unit_cost || 0),
      'HPP Total (Rp)': Number(p.hpp_total || 0) || Number(p.unit_cost || 0) * Number(p.total_quantity || 0),
      'Total Laba (Rp)': Number(p.total_laba || 0),
      'Margin %': Number(p.margin_percent || 0).toFixed(2),
      'Harga Rata-rata (Rp)': Math.round(Number(p.avg_price || 0)),
      'Jumlah Transaksi': p.total_transactions,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Produk & Add-Ons');
    XLSX.writeFile(wb, `laporan-produk-${periodLabel.replace(/[^a-zA-Z0-9-]/g, '-')}.xlsx`);
  };

  const handleExportStock = () => {
    const wb = XLSX.utils.book_new();
    const rows = stockReport.map((r: any) => ({
      'Cabang': r.branch_name,
      'Kode Cabang': r.branch_code,
      'Nama Add-On': r.product_name,
      'Tipe': r.category_name || 'Konsumsi',
      'Gudang': r.gudang_quantity ?? 0,
      'Chiller': r.chiller_quantity ?? 0,
      'Status': r.stock_status === 'LOW' ? 'Habis' : r.stock_status === 'HIGH' ? 'Aman' : 'Hampir Habis',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Stok Add-Ons');
    XLSX.writeFile(wb, `laporan-stok-${periodLabel.replace(/[^a-zA-Z0-9-]/g, '-')}.xlsx`);
  };

  const handleExportVoid = () => {
    const wb = XLSX.utils.book_new();
    const rows = voidedReport.map((r: any) => ({
      'No Invoice': r.transaction_number,
      'Cabang': r.branch_name,
      'Kasir': r.cashier_name || '-',
      'Konsumen': r.customer_name || '-',
      'Metode Bayar': paymentLabel(r.payment_method || ''),
      'Total (Rp)': Number(r.total_amount || 0),
      'Waktu Transaksi': r.created_at ? new Date(r.created_at).toLocaleString('id-ID') : '-',
      'Waktu Void': r.voided_at ? new Date(r.voided_at).toLocaleString('id-ID') : '-',
      'Alasan Void': r.void_reason || '-',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Transaksi Void');
    XLSX.writeFile(wb, `laporan-void-${periodLabel.replace(/[^a-zA-Z0-9-]/g, '-')}.xlsx`);
  };

  const handleExportBundling = () => {
    const wb = XLSX.utils.book_new();
    const rows = bundledReport.map((r: any) => ({
      'No Invoice': r.transaction_number,
      'Cabang': r.branch_name,
      'Kasir': r.cashier_name || '-',
      'Konsumen': r.customer_name || '-',
      'Metode Bayar': paymentLabel(r.payment_method || ''),
      'Total (Rp)': Number(r.total_amount || 0),
      'Waktu Transaksi': r.created_at ? new Date(r.created_at).toLocaleString('id-ID') : '-',
      'Waktu Bundling': r.bundled_at ? new Date(r.bundled_at).toLocaleString('id-ID') : '-',
      'Alasan Bundling': r.bundling_reason || '-',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Transaksi Bundling');
    XLSX.writeFile(wb, `laporan-bundling-${periodLabel.replace(/[^a-zA-Z0-9-]/g, '-')}.xlsx`);
  };

  const handleExportFree = () => {
    const wb = XLSX.utils.book_new();
    const rows = freeReport.map((r: any) => ({
      'No Invoice': r.transaction_number,
      'Cabang': r.branch_name,
      'Kasir': r.cashier_name || '-',
      'Konsumen': r.customer_name || '-',
      'Diskon Diberikan (Rp)': Number(r.discount_amount || 0),
      'Keterangan': (r.notes || '').replace('[GRATIS]', '').trim() || '-',
      'Waktu Transaksi': r.created_at ? new Date(r.created_at).toLocaleString('id-ID') : '-',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Transaksi Gratis');
    XLSX.writeFile(wb, `laporan-gratis-${periodLabel.replace(/[^a-zA-Z0-9-]/g, '-')}.xlsx`);
  };

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.headerTitle}>Performa Penjualan Bulanan</h1>
        </div>
        <div style={s.headerControls}>
          {/* Mode toggle */}
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>Periode</label>
            <div style={{ display: 'flex', border: '1px solid #d1d5db', borderRadius: 7, overflow: 'hidden' }}>
              {(['month', 'custom'] as const).map(m => (
                <button key={m} onClick={() => setDateMode(m)}
                  style={{ padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    background: dateMode === m ? '#1A73E8' : '#fff',
                    color: dateMode === m ? '#fff' : '#374151' }}>
                  {m === 'month' ? 'Bulanan' : 'Kustom'}
                </button>
              ))}
            </div>
          </div>

          {/* Date inputs */}
          {dateMode === 'month' ? (
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Bulan</label>
              <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={s.monthInput} />
            </div>
          ) : (
            <>
              <div style={s.filterGroup}>
                <label style={s.filterLabel}>Dari</label>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={s.monthInput} />
              </div>
              <div style={s.filterGroup}>
                <label style={s.filterLabel}>Sampai</label>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={s.monthInput} />
              </div>
            </>
          )}

          {canFilterAllBranches ? (
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Semua Cabang</label>
              <select value={selectedBranch} onChange={e => setSelectedBranch(e.target.value)} style={s.select}>
                <option value="all">Semua Cabang</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          ) : (
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Cabang</label>
              <div style={{ ...s.select, background: '#f0f4ff', color: '#667eea', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                {branches.find(b => b.id === user?.branch_id)?.name || 'Cabang Saya'}
              </div>
            </div>
          )}
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>Semua Metode</label>
            <select value={selectedPayment} onChange={e => setSelectedPayment(e.target.value)} style={s.select}>
              <option value="all">Semua Metode Pembayaran</option>
              <option value="cash">Tunai</option>
              <option value="qris">QRIS</option>
            </select>
          </div>
          {activeTab === 'transaksi' && (
            <>
              <button style={s.exportBtn} onClick={handleExportExcel}>📊 Excel</button>
              <button style={{ ...s.exportBtn, background: '#E74C3C' }} onClick={handleExportPDF}>📄 PDF</button>
            </>
          )}
          {activeTab === 'stok' && (
            <button style={s.exportBtn} onClick={handleExportStock}>📊 Export Stok</button>
          )}
          {activeTab === 'addons' && (
            <button style={s.exportBtn} onClick={handleExportAddons}>📊 Export Add-Ons</button>
          )}
          {activeTab === 'void' && (
            <button style={{ ...s.exportBtn, background: '#e74c3c' }} onClick={handleExportVoid}>📊 Export Void</button>
          )}
          {activeTab === 'bundling' && (
            <button style={{ ...s.exportBtn, background: '#8e44ad' }} onClick={handleExportBundling}>📊 Export Bundling</button>
          )}
          {activeTab === 'free' && (
            <button style={{ ...s.exportBtn, background: '#e67e22' }} onClick={handleExportFree}>📊 Export Gratis</button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'white', padding: '6px 8px', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflowX: 'auto' as const }}>
        {([
          { key: 'transaksi', label: '💳 Laporan Transaksi' },
          { key: 'addons', label: '📦 Produk & Add-Ons' },
          { key: 'stok', label: '📋 Stok Cabang' },
          { key: 'void', label: '🚫 Transaksi Void' },
          { key: 'bundling', label: '📦 Bundling' },
          { key: 'free', label: '🎁 Transaksi Gratis' },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '9px 18px', border: 'none', borderRadius: 6, cursor: 'pointer',
              fontWeight: activeTab === tab.key ? 700 : 400,
              background: activeTab === tab.key ? '#667eea' : 'transparent',
              color: activeTab === tab.key ? 'white' : '#555',
              fontSize: 13, whiteSpace: 'nowrap' as const,
              transition: 'all 0.15s',
            }}
          >{tab.label}</button>
        ))}
      </div>

      {loading && (
        <div style={s.centered}>
          <div style={s.spinner} />
          <p style={{ color: '#64748b', marginTop: 12 }}>Memuat laporan...</p>
        </div>
      )}

      {error && !loading && (
        <div style={s.errorBox}>
          <p style={s.errorText}>{error}</p>
          <button style={s.retryBtn} onClick={fetchAll}>Coba Lagi</button>
        </div>
      )}

      {/* ===== TAB: PRODUK & ADD-ONS ===== */}
      {!loading && !error && activeTab === 'addons' && (
        <>
          {/* Sewa vs Jual filter chips */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {(['all', 'sewa', 'jual'] as const).map(f => (
              <button key={f}
                onClick={() => setAddonTypeFilter(f)}
                style={{ padding: '7px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
                  fontWeight: addonTypeFilter === f ? 700 : 400,
                  background: addonTypeFilter === f ? (f === 'sewa' ? '#1A73E8' : f === 'jual' ? '#e67e22' : '#667eea') : '#f1f5f9',
                  color: addonTypeFilter === f ? '#fff' : '#64748b', fontSize: 13 }}>
                {f === 'all' ? 'Semua' : f === 'sewa' ? '🎾 Sewa' : '📦 Jual'}
              </button>
            ))}
          </div>
          {/* Product Charts */}
          {(() => {
            const filteredProductSales = productSales.filter((p: any) => {
              if (addonTypeFilter === 'sewa') return p.category_name?.toLowerCase() === 'jasa';
              if (addonTypeFilter === 'jual') return p.category_name?.toLowerCase() !== 'jasa';
              return true;
            });
            return (
          <>
          {filteredProductSales.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div style={s.chartCard}>
                <h3 style={{ ...s.chartTitle, marginBottom: 12 }}>Top Produk (Omzet)</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {filteredProductSales.slice(0, 6).map((p: any, i: number) => {
                    const maxSales = Math.max(...filteredProductSales.slice(0, 6).map((x: any) => Number(x.total_sales || 0)), 1);
                    const pct = (Number(p.total_sales || 0) / maxSales) * 100;
                    return (
                      <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{p.name}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#27ae60' }}>{formatCurrency(Number(p.total_sales || 0))}</span>
                        </div>
                        <div style={s.hBar}>
                          <div style={{ ...s.hBarFill, width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={s.chartCard}>
                <h3 style={{ ...s.chartTitle, marginBottom: 12 }}>Margin Keuntungan (%)</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[...filteredProductSales]
                    .filter((p: any) => Number(p.total_sales || 0) > 0)
                    .sort((a: any, b: any) => Number(b.margin_percent || 0) - Number(a.margin_percent || 0))
                    .slice(0, 6)
                    .map((p: any, i: number) => {
                      const margin = Number(p.margin_percent || 0);
                      const pct = Math.min(Math.abs(margin), 100);
                      const color = margin >= 50 ? '#27ae60' : margin >= 30 ? '#f39c12' : '#e74c3c';
                      return (
                        <div key={i}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{p.name}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color }}>{margin.toFixed(1)}%</span>
                          </div>
                          <div style={s.hBar}>
                            <div style={{ ...s.hBarFill, width: `${pct}%`, background: color }} />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}
          <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' as const }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee' }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>📦 Laporan Penjualan Produk & Add-Ons</h3>
            <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>{filteredProductSales.length} produk terjual di periode ini</p>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' as const }}>
            <thead><tr style={{ background: '#f8fafc' }}>
              {['Nama Add-On','Tipe','Qty Terjual','Total Penjualan','HPP / Unit','HPP Total','Laba','Margin %','Harga Rata-rata','Transaksi'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left' as const, fontSize: 11, fontWeight: 700, color: '#888', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' as const }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filteredProductSales.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Tidak ada data penjualan add-ons untuk periode ini</td></tr>
              ) : filteredProductSales.map((p: any) => (
                <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600 }}>{p.name}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      background: p.category_name === 'Jasa' ? '#e8f4fd' : p.category_name === 'Fnb' ? '#fff3e0' : '#e8f5e9',
                      color: p.category_name === 'Jasa' ? '#1565c0' : p.category_name === 'Fnb' ? '#e65100' : '#2e7d32' }}>
                      {p.category_name === 'Fnb' ? 'FNB' : p.category_name || 'Konsumsi'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 13, textAlign: 'center' as const, fontWeight: 700 }}>{Number(p.total_quantity || 0).toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, color: '#27ae60' }}>Rp{Number(p.total_sales || 0).toLocaleString()}</td>
                  {Number(p.unit_cost || 0) > 0 ? (
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#374151' }}>Rp{Number(p.unit_cost).toLocaleString()}</td>
                  ) : (
                    <td style={{ padding: '10px 12px', fontSize: 13, color: '#94a3b8' }} title="HPP belum diisi">—</td>
                  )}
                  {Number(p.unit_cost || 0) > 0 ? (
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, color: '#e67e22' }}>Rp{Number(p.hpp_total || 0).toLocaleString()}</td>
                  ) : (
                    <td style={{ padding: '10px 12px', fontSize: 13, color: '#94a3b8' }}>—</td>
                  )}
                  {Number(p.unit_cost || 0) > 0 ? (
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, color: (p.total_laba ?? 0) >= 0 ? '#27ae60' : '#e74c3c', background: (p.total_laba ?? 0) >= 0 ? '#f0f9f0' : '#fef0f0', borderRadius: 4 }}>
                      {(p.total_laba ?? 0) >= 0 ? '+' : '−'}Rp{Math.abs(Number(p.total_laba || 0)).toLocaleString('id-ID')}
                    </td>
                  ) : (
                    <td style={{ padding: '10px 12px', fontSize: 13, color: '#94a3b8' }}>—</td>
                  )}
                  {Number(p.unit_cost || 0) > 0 ? (
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600, color: Number(p.margin_percent ?? 0) >= 30 ? '#27ae60' : Number(p.margin_percent ?? 0) < 0 ? '#e74c3c' : '#f39c12' }}>
                      {(Number(p.margin_percent ?? 0)).toFixed(1)}%
                    </td>
                  ) : (
                    <td style={{ padding: '10px 12px', fontSize: 13, color: '#94a3b8' }} title="HPP belum diisi">—</td>
                  )}
                  <td style={{ padding: '10px 12px', fontSize: 13 }}>Rp{Math.round(Number(p.avg_price || 0)).toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', fontSize: 13, textAlign: 'center' as const }}>{p.total_transactions}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </>
            );
          })()}
        </>
      )}

      {/* ===== TAB: STOK ===== */}
      {!loading && !error && activeTab === 'stok' && (
        <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' as const }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16 }}>📋 Laporan Stok Cabang</h3>
              <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>{stockReport.length} item stok di semua cabang</p>
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
              <span style={{ color: '#e74c3c', fontWeight: 600 }}>🔴 Chiller Habis: {stockReport.filter((r: any) => r.stock_status === 'LOW').length}</span>
              <span style={{ color: '#f39c12', fontWeight: 600 }}>🟡 Hampir Habis: {stockReport.filter((r: any) => r.stock_status === 'NORMAL').length}</span>
              <span style={{ color: '#27ae60', fontWeight: 600 }}>🟢 Aman: {stockReport.filter((r: any) => r.stock_status === 'HIGH').length}</span>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' as const }}>
            <thead><tr style={{ background: '#f8fafc' }}>
              {['Cabang','Nama Add-On','Tipe','Gudang','Chiller','Status'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left' as const, fontSize: 11, fontWeight: 700, color: '#888', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' as const }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {stockReport.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Tidak ada data stok konsumsi</td></tr>
              ) : stockReport.map((r: any) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0', background: r.stock_status === 'LOW' ? '#fff8f8' : 'white' }}>
                  <td style={{ padding: '10px 12px', fontSize: 13 }}>
                    {r.branch_name}<br/><span style={{ fontSize: 11, color: '#999' }}>{r.branch_code}</span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600 }}>{r.product_name}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      background: '#e8f5e9', color: '#2e7d32' }}>{r.category_name}</span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 600, textAlign: 'center' as const, color: '#64748b' }}>{r.gudang_quantity ?? 0}</td>
                  <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 700, textAlign: 'center' as const }}>{r.chiller_quantity ?? 0}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: 'white',
                      background: r.stock_status === 'LOW' ? '#e74c3c' : r.stock_status === 'HIGH' ? '#27ae60' : '#f39c12' }}>
                      {r.stock_status === 'LOW' ? 'Habis' : r.stock_status === 'HIGH' ? 'Aman' : 'Hampir Habis'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== TAB: VOID ===== */}
      {!loading && !error && activeTab === 'void' && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
            <div style={{ ...s.metricCard, flex: 1 }}>
              <p style={s.metricLabel}>Total Transaksi Void</p>
              <h2 style={{ ...s.metricValue, color: '#e74c3c', fontSize: 28 }}>{voidedSummary.total_voided}</h2>
              <p style={s.metricSub}>transaksi dibatalkan di periode ini</p>
            </div>
            <div style={{ ...s.metricCard, flex: 1.4 }}>
              <p style={s.metricLabel}>Total Nilai yang Dibatalkan</p>
              <h2 style={{ ...s.metricValue, color: '#e74c3c', fontSize: 28 }}>{formatCurrency(voidedSummary.total_amount)}</h2>
              <p style={s.metricSub}>pendapatan yang tidak jadi tercatat</p>
            </div>
          </div>
          {/* Table */}
          <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' as const }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee' }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>🚫 Daftar Transaksi Void</h3>
              <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>{voidedReport.length} transaksi void di periode ini</p>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' as const }}>
              <thead><tr style={{ background: '#fef2f2' }}>
                {['No Invoice','Cabang','Kasir','Konsumen','Metode','Total','Tgl Transaksi','Tgl Void','Alasan Void'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left' as const, fontSize: 11, fontWeight: 700, color: '#c0392b', borderBottom: '2px solid #fca5a5', whiteSpace: 'nowrap' as const }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {voidedReport.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: 48, color: '#999' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                    <div style={{ fontWeight: 600 }}>Tidak ada transaksi void di periode ini</div>
                  </td></tr>
                ) : voidedReport.map((r: any) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0', background: '#fffafa' }}>
                    <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#e74c3c' }}>{r.transaction_number}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12 }}>{r.branch_name}<br/><span style={{ fontSize: 10, color: '#999' }}>{r.branch_code}</span></td>
                    <td style={{ padding: '10px 12px', fontSize: 12 }}>{r.cashier_name || '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12 }}>{r.customer_name || '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: PAYMENT_COLORS[r.payment_method] ? '#f0f4ff' : '#f5f5f5', color: PAYMENT_COLORS[r.payment_method] || '#666' }}>
                        {paymentLabel(r.payment_method || '')}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#e74c3c', textDecoration: 'line-through' }}>{formatCurrency(Number(r.total_amount || 0))}</td>
                    <td style={{ padding: '10px 12px', fontSize: 11, color: '#666' }}>{r.created_at ? new Date(r.created_at).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 11, color: '#e74c3c' }}>{r.voided_at ? new Date(r.voided_at).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12, fontStyle: 'italic', color: '#555', maxWidth: 200 }}>{r.void_reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
              {voidedReport.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#fef2f2', borderTop: '2px solid #fca5a5' }}>
                    <td colSpan={5} style={{ padding: '10px 12px', fontSize: 13, fontWeight: 800, color: '#c0392b' }}>TOTAL VOID</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 800, color: '#e74c3c', textDecoration: 'line-through' }}>{formatCurrency(voidedSummary.total_amount)}</td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}

      {/* ===== TAB: BUNDLING ===== */}
      {!loading && !error && activeTab === 'bundling' && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
            <div style={{ ...s.metricCard, flex: 1 }}>
              <p style={s.metricLabel}>Total Transaksi Bundling</p>
              <h2 style={{ ...s.metricValue, color: '#8e44ad', fontSize: 28 }}>{bundledSummary.total_bundled}</h2>
              <p style={s.metricSub}>transaksi dibundling di periode ini</p>
            </div>
            <div style={{ ...s.metricCard, flex: 1.4 }}>
              <p style={s.metricLabel}>Total Nilai Bundling</p>
              <h2 style={{ ...s.metricValue, color: '#8e44ad', fontSize: 28 }}>{formatCurrency(bundledSummary.total_amount)}</h2>
              <p style={s.metricSub}>nilai transaksi yang dipindahkan ke bundling</p>
            </div>
          </div>
          {/* Table */}
          <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' as const }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee' }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>📦 Daftar Transaksi Bundling</h3>
              <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>{bundledReport.length} transaksi bundling di periode ini</p>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' as const }}>
              <thead><tr style={{ background: '#f5eeff' }}>
                {['No Invoice','Cabang','Kasir','Konsumen','Metode','Total','Item','Tgl Transaksi','Tgl Bundling','Alasan'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left' as const, fontSize: 11, fontWeight: 700, color: '#6c3483', borderBottom: '2px solid #d7bde2', whiteSpace: 'nowrap' as const }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {bundledReport.length === 0 ? (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: 48, color: '#999' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                    <div style={{ fontWeight: 600 }}>Tidak ada transaksi bundling di periode ini</div>
                  </td></tr>
                ) : bundledReport.map((r: any) => {
                  const addons: any[] = Array.isArray(r.addons) ? r.addons : [];
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0', background: '#fdf9ff' }}>
                      <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#8e44ad' }}>{r.transaction_number}</td>
                      <td style={{ padding: '10px 12px', fontSize: 12 }}>{r.branch_name}<br/><span style={{ fontSize: 10, color: '#999' }}>{r.branch_code}</span></td>
                      <td style={{ padding: '10px 12px', fontSize: 12 }}>{r.cashier_name || '-'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 12 }}>{r.customer_name || '-'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 12 }}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: '#f5f5f5', color: '#666' }}>
                          {paymentLabel(r.payment_method || '')}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#8e44ad' }}>{formatCurrency(Number(r.total_amount || 0))}</td>
                      <td style={{ padding: '10px 12px', fontSize: 11, color: '#555', maxWidth: 180 }}>
                        {addons.length === 0 ? '-' : addons.slice(0, 3).map((a, i) => (
                          <div key={i}>{a.name} ×{a.qty}</div>
                        ))}
                        {addons.length > 3 && <div style={{ color: '#94a3b8' }}>+{addons.length - 3} lainnya</div>}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 11, color: '#666' }}>{r.created_at ? new Date(r.created_at).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 11, color: '#8e44ad' }}>{r.bundled_at ? new Date(r.bundled_at).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 12, fontStyle: 'italic', color: '#555', maxWidth: 180 }}>{r.bundling_reason || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
              {bundledReport.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#f5eeff', borderTop: '2px solid #d7bde2' }}>
                    <td colSpan={5} style={{ padding: '10px 12px', fontSize: 13, fontWeight: 800, color: '#6c3483' }}>TOTAL BUNDLING</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 800, color: '#8e44ad' }}>{formatCurrency(bundledSummary.total_amount)}</td>
                    <td colSpan={4} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}

      {/* ===== TAB: FREE ===== */}
      {!loading && !error && activeTab === 'free' && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
            <div style={{ background: 'white', borderRadius: 10, padding: '20px 24px', flex: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <p style={{ margin: 0, fontSize: 13, color: '#666', fontWeight: 600 }}>Total Transaksi Gratis</p>
              <h2 style={{ margin: '6px 0 0', fontSize: 28, fontWeight: 800, color: '#e67e22' }}>{freeSummary.total_free}</h2>
              <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>{freeReport.length} transaksi gratis di periode ini</p>
            </div>
            <div style={{ background: 'white', borderRadius: 10, padding: '20px 24px', flex: 1.4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <p style={{ margin: 0, fontSize: 13, color: '#666', fontWeight: 600 }}>Total Diskon Diberikan</p>
              <h2 style={{ margin: '6px 0 0', fontSize: 28, fontWeight: 800, color: '#e67e22' }}>{formatCurrency(freeSummary.total_discount)}</h2>
              <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>nilai yang dibebaskan dari pembayaran</p>
            </div>
          </div>
          {/* Table */}
          <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' as const }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee' }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>🎁 Daftar Transaksi Gratis</h3>
              <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>{freeReport.length} transaksi gratis di periode ini</p>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' as const }}>
              <thead><tr style={{ background: '#fff8f0' }}>
                {['No Invoice','Cabang','Kasir','Konsumen','Diskon (Rp)','Keterangan','Tgl Transaksi'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left' as const, fontSize: 11, fontWeight: 700, color: '#b45309', borderBottom: '2px solid #fed7aa', whiteSpace: 'nowrap' as const }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {freeReport.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 48, color: '#999' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>🎁</div>
                    <div style={{ fontWeight: 600 }}>Tidak ada transaksi gratis di periode ini</div>
                  </td></tr>
                ) : freeReport.map((r: any) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0', background: '#fffbf7' }}>
                    <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#e67e22' }}>{r.transaction_number}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12 }}>{r.branch_name}<br/><span style={{ fontSize: 10, color: '#999' }}>{r.branch_code}</span></td>
                    <td style={{ padding: '10px 12px', fontSize: 12 }}>{r.cashier_name || '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12 }}>{r.customer_name || '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#e67e22' }}>{formatCurrency(Number(r.discount_amount || 0))}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12, fontStyle: 'italic', color: '#555', maxWidth: 220 }}>{(r.notes || '').replace('[GRATIS]', '').trim() || '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 11, color: '#666' }}>{r.created_at ? new Date(r.created_at).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                  </tr>
                ))}
              </tbody>
              {freeReport.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#fff8f0', borderTop: '2px solid #fed7aa' }}>
                    <td colSpan={4} style={{ padding: '10px 12px', fontSize: 13, fontWeight: 800, color: '#b45309' }}>TOTAL GRATIS</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 800, color: '#e67e22' }}>{formatCurrency(freeSummary.total_discount)}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}

      {!loading && !error && activeTab === 'transaksi' && summary && (
        <>
          {/* 4 Metric Cards */}
          <div style={s.metricsRow}>
            <MetricCard
              label="Total Pendapatan Bulanan"
              value={formatCurrency(summary.total_revenue)}
              sub={`vs Rp ${(summary.total_revenue * 0.92).toFixed(0)} bulan lalu`}
              color="#1A73E8"
              large
            />
            <MetricCard
              label="Total HPP"
              value={formatCurrency(summary.total_hpp || 0)}
              sub={`Laba: ${formatCurrency(summary.total_revenue - (summary.total_hpp || 0))}`}
              color="#e67e22"
            />
            <MetricCard
              label="PIH Cabang"
              value={formatCurrency(highestBranchRevenue)}
              sub={summary.by_branch.find(b => b.revenue === highestBranchRevenue)?.branch_name || '—'}
              color="#27AE60"
            />
            <MetricCard
              label="Rata-rata per Transaksi"
              value={formatCurrency(summary.average_transaction)}
              sub={`${summary.total_transactions.toLocaleString('id-ID')} total transaksi`}
              color="#9b59b6"
            />
          </div>

          <div style={s.twoCol}>
            {/* Daily Revenue Bar Chart */}
            <div style={s.chartCard}>
              <div style={s.chartHeader}>
                <h3 style={s.chartTitle}>Tren Pendapatan Harian</h3>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{dailyAggregated.length} hari</span>
              </div>
              {dailyAggregated.length === 0 ? (
                <div style={s.noData}><p style={{ color: '#94a3b8', fontSize: 14 }}>Tidak ada data</p></div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 140, padding: '0 4px', marginTop: 8 }}>
                    {dailyAggregated.map((d, i) => {
                      const maxRev = Math.max(...dailyAggregated.map(x => x.revenue), 1);
                      const barH = Math.max(Math.round((d.revenue / maxRev) * 110), 2);
                      const isHov = hoveredBar === i;
                      return (
                        <div key={i}
                          style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', position: 'relative', cursor: 'pointer', height: '100%' }}
                          onMouseEnter={() => setHoveredBar(i)}
                          onMouseLeave={() => setHoveredBar(null)}>
                          {isHov && (
                            <div style={s.tooltip}>
                              <p style={s.tooltipDate}>{new Date(d.date + 'T00:00:00').toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}</p>
                              <p style={s.tooltipVal}>{formatCurrency(d.revenue)}</p>
                            </div>
                          )}
                          <div style={{ flex: 1 }} />
                          <div style={{ width: '100%', height: `${barH}px`, background: isHov ? '#1557c0' : '#1A73E8', borderRadius: '3px 3px 0 0', transition: 'height 0.3s, background 0.15s' }} />
                          <span style={{ fontSize: 9, color: '#94a3b8', marginTop: 4 }}>{d.date.slice(8, 10)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Total: {formatCurrency(dailyAggregated.reduce((sum, d) => sum + d.revenue, 0))}</span>
                    <span>Rata-rata: {formatCurrency(Math.round(dailyAggregated.reduce((sum, d) => sum + d.revenue, 0) / Math.max(dailyAggregated.length, 1)))}/hari</span>
                  </div>
                </>
              )}
            </div>

            {/* Per-Branch Horizontal Bar Chart */}
            <div style={s.chartCard}>
              <h3 style={s.chartTitle}>Penjualan per Cabang</h3>
              {summary.by_branch.length === 0 ? (
                <div style={s.noData}><p style={{ color: '#94a3b8', fontSize: 14 }}>Tidak ada data cabang</p></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
                  {summary.by_branch.map((b, i) => {
                    const maxRev = Math.max(...summary.by_branch.map(x => x.revenue), 1);
                    const pct = (b.revenue / maxRev) * 100;
                    return (
                      <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{b.branch_name}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#27ae60' }}>{b.transactions.toLocaleString('id-ID')}</span>
                        </div>
                        <div style={s.hBar}>
                          <div style={{ ...s.hBarFill, width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Branch Performance Table */}
          <div style={s.tableCard}>
            <div style={s.tableHeader}>
              <h3 style={s.chartTitle}>Rincian Performa Cabang</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={s.tableBtn} onClick={handleExportPDF}>📄 PDF</button>
                <button style={s.tableBtn} onClick={handleExportExcel}>📊 Excel</button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr style={s.thead}>
                    <th style={s.th}>NAMA CABANG</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>PENDAPATAN</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>HPP</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>PESANAN</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>TARIF RATA2</th>
                    <th style={s.th}>PEMBAYARAN TERTINGGI</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {branchWithStatus.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>
                        Tidak ada data cabang pada periode ini
                      </td>
                    </tr>
                  ) : branchWithStatus.map((b, i) => (
                    <tr key={i} style={s.tr}>
                      <td style={{ ...s.td, fontWeight: 600, color: '#1e293b' }}>{b.branch_name}</td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                        {formatCurrency(b.revenue)}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 600, color: '#e67e22' }}>
                        {formatCurrency(b.hpp || 0)}
                      </td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{b.transactions.toLocaleString('id-ID')}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>
                        {formatCurrency(b.transactions > 0 ? b.revenue / b.transactions : 0)}
                      </td>
                      <td style={s.td}>
                        <span style={{ ...s.payDot, background: PAYMENT_COLORS[b.topPayment] || '#64748b' }} />
                        {paymentLabel(b.topPayment)}
                      </td>
                      <td style={{ ...s.td, textAlign: 'center' }}>
                        <StatusBadge status={b.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                {branchWithStatus.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                      <td style={{ ...s.td, fontWeight: 800, color: '#1e293b', fontSize: 13 }}>TOTAL KESELURUHAN</td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 800, color: '#1A73E8', fontSize: 13 }}>
                        {formatCurrency(summary.total_revenue)}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 800, color: '#e67e22', fontSize: 13 }}>
                        {formatCurrency(summary.total_hpp || 0)}
                      </td>
                      <td style={{ ...s.td, textAlign: 'center', fontWeight: 800, color: '#1e293b', fontSize: 13 }}>
                        {summary.total_transactions.toLocaleString('id-ID')}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: '#1e293b', fontSize: 13 }}>
                        {formatCurrency(summary.average_transaction)}
                      </td>
                      <td style={s.td} />
                      <td style={s.td} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {summary.total_transactions === 0 && (
              <div style={s.noData}>
                <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: '#64748b' }}>Tidak ada transaksi pada periode ini</p>
                <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>Coba pilih bulan yang berbeda</p>
              </div>
            )}
          </div>

          {/* Payment Method Breakdown */}
          {summary.by_payment.length > 0 && (() => {
            const payTotal = summary.by_payment.reduce((s, p) => s + p.amount, 0);
            return (
            <div style={s.tableCard}>
              <h3 style={{ ...s.chartTitle, marginBottom: 16 }}>Pembayaran per Metode</h3>
              <div style={{ display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' as const }}>
                {/* SVG Donut Chart */}
                {(() => {
                  const r = 50, cx = 70, cy = 70;
                  const circ = 2 * Math.PI * r;
                  const segments: Array<{ dash: number; offset: number; color: string }> = [];
                  let cumulative = 0;
                  summary.by_payment.forEach(p => {
                    const pct = payTotal > 0 ? p.amount / payTotal : 0;
                    const dash = pct * circ;
                    segments.push({ dash, offset: circ * 0.25 - cumulative, color: PAYMENT_COLORS[p.method] || '#64748b' });
                    cumulative += dash;
                  });
                  return (
                    <svg width={140} height={140} viewBox="0 0 140 140" style={{ flexShrink: 0 }}>
                      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={24} />
                      {segments.map((seg, i) => (
                        <circle key={i} cx={cx} cy={cy} r={r}
                          fill="none" stroke={seg.color} strokeWidth={24}
                          strokeDasharray={`${seg.dash} ${circ}`}
                          strokeDashoffset={seg.offset}
                        />
                      ))}
                      <text x="70" y="65" textAnchor="middle" fontSize="11" fill="#94a3b8">Total</text>
                      <text x="70" y="84" textAnchor="middle" fontSize="18" fontWeight={700} fill="#1e293b">
                        {summary.total_transactions.toLocaleString('id-ID')}
                      </text>
                      <text x="70" y="100" textAnchor="middle" fontSize="10" fill="#64748b">transaksi</text>
                    </svg>
                  );
                })()}
                {/* Horizontal Bars */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {summary.by_payment.map((p, i) => {
                    const pct = payTotal > 0 ? (p.amount / payTotal) * 100 : 0;
                    const color = PAYMENT_COLORS[p.method] || '#64748b';
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 160 }}>
                          <span style={{ ...s.payDot, background: color }} />
                          <div>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{paymentLabel(p.method)}</p>
                            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#94a3b8' }}>{p.count.toLocaleString('id-ID')} transaksi</p>
                          </div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ flex: 1, height: 12, background: '#e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 6, transition: 'width 0.4s' }} />
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', minWidth: 130, textAlign: 'right' }}>
                            {formatCurrency(p.amount)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

const MetricCard: React.FC<{ label: string; value: string; sub: string; color: string; large?: boolean }> = ({ label, value, sub, color, large }) => (
  <div style={{ ...s.metricCard, flex: large ? 1.4 : 1 }}>
    <p style={s.metricLabel}>{label}</p>
    <h2 style={{ ...s.metricValue, color, fontSize: large ? 28 : 22 }}>{value}</h2>
    <p style={s.metricSub}>{sub}</p>
  </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    capai: { label: 'Capai Target', color: '#27AE60', bg: '#EAFAF1' },
    hampir: { label: 'Hampir', color: '#F39C12', bg: '#FEF9E7' },
    bawah: { label: 'Di Bawah Target', color: '#E74C3C', bg: '#FDEDEC' },
  };
  const { label, color, bg } = map[status] || map.bawah;
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color, background: bg }}>
      {label}
    </span>
  );
};

const s: Record<string, React.CSSProperties> = {
  container: { padding: 24, background: '#f5f7fa', minHeight: '100vh' },
  centered: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0' },
  spinner: { width: 36, height: 36, border: '3px solid #e2e8f0', borderTop: '3px solid #1A73E8', borderRadius: '50%' },

  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    background: '#fff', padding: '20px 24px', borderRadius: 12, marginBottom: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 12,
  },
  headerTitle: { margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b' },
  headerControls: { display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  filterLabel: { fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.3 },
  monthInput: {
    padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 13, color: '#374151', background: '#fff',
  },
  select: {
    padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 13, color: '#374151', background: '#fff',
  },
  filterBtn: {
    padding: '7px 12px', background: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 13, color: '#475569', cursor: 'pointer', alignSelf: 'flex-end',
  },
  exportBtn: {
    padding: '7px 14px', background: '#1A73E8', border: 'none',
    borderRadius: 8, fontSize: 13, color: '#fff', cursor: 'pointer', fontWeight: 600, alignSelf: 'flex-end',
  },

  errorBox: {
    background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 12,
    padding: 24, textAlign: 'center', marginBottom: 24,
  },
  errorText: { color: '#dc2626', margin: '0 0 12px', fontSize: 14 },
  retryBtn: {
    padding: '8px 20px', background: '#1A73E8', color: '#fff',
    border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14,
  },

  metricsRow: { display: 'flex', gap: 16, marginBottom: 20 },
  metricCard: {
    background: '#fff', padding: '20px 24px', borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0',
  },
  metricLabel: { margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 },
  metricValue: { margin: '0 0 6px', fontWeight: 800 },
  metricSub: { margin: 0, fontSize: 12, color: '#94a3b8' },

  twoCol: { display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 20, marginBottom: 20 },
  chartCard: {
    background: '#fff', padding: '20px 24px', borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0',
  },
  chartHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  chartTitle: { margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' },

  barChart: { display: 'flex', alignItems: 'flex-end', gap: 3, height: 160, padding: '0 4px' },
  barWrap: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', cursor: 'pointer' },
  barTrack: { flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end', position: 'relative' },
  barFill: { width: '100%', borderRadius: '3px 3px 0 0', transition: 'height 0.3s, background 0.15s', minHeight: 0 },
  barLabel: { fontSize: 10, color: '#94a3b8', marginTop: 4 },
  tooltip: {
    position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
    background: '#1e293b', color: '#fff', padding: '6px 10px', borderRadius: 8,
    fontSize: 11, whiteSpace: 'nowrap', zIndex: 10,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
  tooltipDate: { margin: '0 0 2px', fontWeight: 600 },
  tooltipVal: { margin: 0, color: '#93c5fd' },

  hBar: { height: 10, background: '#e2e8f0', borderRadius: 5, overflow: 'hidden' },
  hBarFill: { height: '100%', background: '#1A73E8', borderRadius: 5, transition: 'width 0.4s' },

  tableCard: {
    background: '#fff', borderRadius: 12, padding: '20px 24px', marginBottom: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0',
  },
  tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  tableBtn: {
    padding: '6px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 13, color: '#475569', cursor: 'pointer',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: '#f8fafc' },
  th: {
    padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700,
    color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: '2px solid #e2e8f0',
  },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '12px 16px', fontSize: 13, color: '#374151' },
  noData: { padding: '32px', textAlign: 'center' },
  payDot: { display: 'inline-block', width: 10, height: 10, borderRadius: '50%', marginRight: 6 },
};
